import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { Reveal } from "../Reveal";
import { useCart } from "../../context/CartContext";
import api from "../../lib/api";

const LOOKS = [
  { src: "/images/lookbook-3.jpg", caption: "Linen & Leisure", detail: "Heritage Argyle × white sneakers", slug: "heritage-argyle", cls: "lg:col-span-5 aspect-[3/4]" },
  { src: "/images/lookbook-1.jpg", caption: "The Parisian Step", detail: "Noir Meridian × burgundy loafers", slug: "noir-meridian", cls: "lg:col-span-4 aspect-[3/4] lg:mt-28" },
  { src: "/images/lookbook-2.jpg", caption: "Marble Hour", detail: "Bordeaux Line × cream heels", slug: "bordeaux-line", cls: "lg:col-span-3 aspect-[3/4] lg:mt-12" },
  { src: "/images/lookbook-4.jpg", caption: "Deco Walk", detail: "Savile Stripe × patent derbies", slug: "savile-stripe", cls: "lg:col-span-6 aspect-[16/10] lg:-mt-20" },
];

export const Lookbook = () => {
  const [products, setProducts] = useState({});
  const [openLook, setOpenLook] = useState(null);
  const [size, setSize] = useState("M");
  const { add, setOpen } = useCart();

  useEffect(() => {
    api.get("/products").then(({ data }) => setProducts(Object.fromEntries(data.map((p) => [p.slug, p])))).catch(() => {});
  }, []);

  return (
  <section id="lookbook" className="relative mx-auto max-w-[1500px] overflow-visible px-6 py-28 md:px-10 md:py-40" data-testid="lookbook-section">
    <div className="mb-16 md:mb-24">
      <Reveal>
        <p className="mb-4 flex items-center gap-3 text-[0.68rem] font-bold uppercase tracking-[0.34em] text-burgundy">
          <span className="h-px w-10 bg-gold" /> The Lookbook
        </p>
        <h2 className="max-w-3xl font-serif text-4xl leading-[1.05] tracking-tight text-navy sm:text-5xl lg:text-6xl" data-testid="lookbook-headline">
          Style Your <em className="text-gold">Steps</em>
        </h2>
        <p className="mt-6 max-w-lg leading-relaxed text-navy/60">
          Four ways the fashion-forward wear Locka — and every look is shoppable. Tap in, pick your size, step out.
        </p>
      </Reveal>
    </div>
    <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-12">
      {LOOKS.map((look, i) => {
        const p = products[look.slug];
        return (
        <motion.figure
          key={look.src}
          initial={{ opacity: 0, y: 70 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 1, delay: (i % 3) * 0.12, ease: [0.22, 1, 0.36, 1] }}
          className={`group relative overflow-hidden ${look.cls}`}
          data-testid={`lookbook-item-${i + 1}`}
        >
          <img
            src={look.src}
            alt={`${look.caption} — ${look.detail}`}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-[1.6s] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]"
          />
          <div className={`absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent transition-opacity duration-600 ${openLook === i ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`} />
          <figcaption className={`absolute bottom-0 left-0 p-6 transition-[transform,opacity] duration-500 ${openLook === i ? "pointer-events-none -translate-y-4 opacity-0" : "translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100"}`}>
            <p className="font-serif text-2xl italic text-white">{look.caption}</p>
            <p className="mt-1 text-[0.62rem] font-bold uppercase tracking-[0.26em] text-gold">{look.detail}</p>
            {p && (
              <button
                onClick={() => { setOpenLook(i); setSize("M"); }}
                data-testid={`shop-the-look-${look.slug}`}
                className="mt-4 border border-gold/60 px-5 py-2.5 text-[0.6rem] font-bold uppercase tracking-[0.26em] text-gold transition-colors duration-300 hover:bg-gold hover:text-[#0B101E]"
              >
                Shop the Look
              </button>
            )}
          </figcaption>
          <span className="absolute right-5 top-5 font-serif text-lg italic text-white/0 transition-colors duration-500 group-hover:text-gold">0{i + 1}</span>
          <AnimatePresence>
            {openLook === i && p && (
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 24 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-x-4 bottom-4 bg-[#0B101E]/95 p-5 backdrop-blur-md"
                data-testid={`shop-the-look-panel-${look.slug}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-serif text-lg italic leading-tight text-white">{p.name}</p>
                    <p className="mt-1 text-[0.62rem] font-bold uppercase tracking-[0.24em] text-gold">${p.price.toFixed(2)}</p>
                  </div>
                  <button onClick={() => setOpenLook(null)} aria-label="Close shop the look" data-testid={`shop-the-look-close-${look.slug}`} className="text-white/60 transition-colors duration-200 hover:text-gold">
                    <X className="h-4 w-4" />
                  </button>
                </div>
                <div className="mt-3.5 flex gap-1.5">
                  {["S", "M", "L"].map((s) => {
                    const out = (p.sizes?.[s] ?? 1) <= 0;
                    return (
                      <button
                        key={s}
                        disabled={out}
                        onClick={() => setSize(s)}
                        data-testid={`shop-the-look-size-${look.slug}-${s.toLowerCase()}`}
                        className={`flex-1 border py-2 text-[0.65rem] font-bold tracking-[0.2em] transition-colors duration-200 ${
                          out
                            ? "cursor-not-allowed border-white/10 text-white/25 line-through"
                            : size === s
                              ? "border-gold bg-gold text-[#0B101E]"
                              : "border-white/25 text-white/70 hover:border-gold hover:text-gold"
                        }`}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => { add(p, size); setOpenLook(null); setOpen(true); }}
                  data-testid={`shop-the-look-add-${look.slug}`}
                  className="mt-3 w-full bg-gold py-3 text-[0.62rem] font-bold uppercase tracking-[0.26em] text-[#0B101E] transition-colors duration-300 hover:bg-white"
                >
                  Add to Cart — Size {size}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.figure>
        );
      })}
      <Reveal className="flex items-center justify-center border border-navy/15 p-10 text-center lg:col-span-6 lg:-mt-20" delay={0.2}>
        <div>
          <p className="font-serif text-3xl italic leading-snug text-navy/80">“Dressed from the ground up.”</p>
          <p className="mt-4 text-[0.62rem] font-bold uppercase tracking-[0.3em] text-navy/45">Tag @locka.maison to be featured</p>
        </div>
      </Reveal>
    </div>
  </section>
  );
};
